import { ActionFunctionArgs } from '@remix-run/node';
import { Link, useNavigate, useSearchParams } from '@remix-run/react';
import { sendSignInLinkToEmail, signOut } from 'firebase/auth';
import { logoutWithBackend } from '~/utils/ServerRequest';
import { useEffect, useState } from 'react';
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { auth } from '~/utils/auth/firebase-service';
import {
  actionCodeSettings,
  loginWithEmailLink,
  loginWithGoogle,
} from '~/utils/auth/signInMethods';
import logo from '../assets/bskFace.png';
import google_png from '../assets/googleLogo.png';
import google_webp from '../assets/googleLogo.webp';
import cumulusLogo from '../assets/CumulusLogo_Black.png';
import '../styles/modal.css';
import { PopupCarousel } from './PopupCarousel';
import LoadingButton from './LoadingButton';
import { Header } from './Header';
import { normalizeEmail } from '~/functions';

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  const email = String(formData.get('email')).toLowerCase();

  // placed here in case there is data left over from a previously authenticated user
  await signOut(auth);
  await logoutWithBackend();

  try {
    if (email) {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    }
  } catch (error) {
    console.error(error);
  }
}

export const PopupModal = (props: {
  toggle: () => void;
  mode: 'login' | 'signUp';
  switchModal: () => void;
  redirectUrl?: string;
}) => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [emailInvalid, setEmailInvalid] = useState<boolean | null>(null);
  const [agreed, setAgreed] = useState<boolean | null>(null);
  const [clickedButton, setClickedButton] = useState<string>('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [keepMeSignedIn, setKeepMeSignedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isEmailValid = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (emailRegex.test(email)) {
      return true;
    } else {
      return false;
    }
  };

  async function handleFormSubmit(e: any) {
    e.preventDefault();

  if (clickedButton == 'create' || clickedButton == 'signIn') {
  if (isEmailValid(email)) {
        // props.toggle();
        setEmailInvalid(false);

        var agreeCheckbox = document.getElementsByName(`_agree`);
        if (
          props.mode != 'signUp' ||
          (agreeCheckbox[0] as HTMLInputElement).checked
        ) {
          setAgreed(true);
          setLoading(true);
          // Set Firebase persistence
          const { setPersistence, browserLocalPersistence, browserSessionPersistence } = await import('firebase/auth');
          await setPersistence(
            auth,
            keepMeSignedIn ? browserLocalPersistence : browserSessionPersistence
          );
          // Optionally pass keepMeSignedIn to backend via query param or body
          loginWithEmailLink(email, props.redirectUrl).then(() => {
            setMagicLinkSent(true);
            setLoading(false);
          });
        } else {
          setAgreed(false);
        }
      } else {
        setEmailInvalid(true);
      }
  } else if (clickedButton == 'google') {
      var agreeCheckbox = document.getElementsByName(`_agree`);
      if (
        props.mode != 'signUp' ||
        (agreeCheckbox[0] as HTMLInputElement).checked
      ) {
        setAgreed(true);
        setLoading(true);
        // Set Firebase persistence
        const { setPersistence, browserLocalPersistence, browserSessionPersistence } = await import('firebase/auth');
        await setPersistence(
          auth,
          keepMeSignedIn ? browserLocalPersistence : browserSessionPersistence
        );
        let success = await loginWithGoogle();

        if (success) {
          if (props.redirectUrl) {
            window.location.href = props.redirectUrl;
            return;
          }
          const acceptCode = searchParams.get('accept');
          if (acceptCode) {
            navigate(`/projects/accept?code=${encodeURIComponent(acceptCode)}`);
          } else {
            navigate('/projects/list');
          }
        }
        setLoading(false);
      } else {
        setAgreed(false);
      }
    } else {
      console.error('Unknown login type');
    }
  }

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        props.toggle();
      }
    };

    window.addEventListener('keydown', close);

    return () => window.removeEventListener('keydown', close);
  }, []);

  return (
    <div className="popup">
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1001,
          width: '100%',
        }}
      >
        <Header
          disableModals
          onLogoClick={() => {
            props.toggle();
            navigate('/home?page=login');
          }}
          modal={props.mode === 'signUp' ? 'signup' : 'login'}
          switchModal={props.switchModal}
        />
      </div>
      <div id="popupContent">
        <div className="popup-left">
          <div className="rowArrangement" id="topAccountRow">
            <button id="modalToggle" onClick={() => props.toggle()}>
              <IoIosCloseCircleOutline style={{ fontSize: '40px' }} />
            </button>
            <div className="rowArrangement" id="topAccountRowRight">
              <p className="">
                {props.mode == 'signUp'
                  ? `Already have an account?`
                  : `New to Cumulus?`}
              </p>
              <button
                className="popupModalButton"
                onClick={() => props.switchModal()}
              >
                {props.mode == 'signUp' ? `Log In` : `Create Account`}
              </button>
            </div>
          </div>
          <div id="popupLeftContent">
            <div id="popupLogoHeader">
              <img
                src={logo}
                width={'100vw'}
                id="popupModalLogo"
                alt="CUMULUS"
              />
            </div>
            {magicLinkSent ? (
              props.mode == 'signUp' ? (
                <>
                  <p className="boldText" id="popupLogoHeaderText">
                    Cumulus
                  </p>
                  <p className="boldText margin0">
                    Expressed Behaviour Analytics Platform
                  </p>
                  <p className="boldText margin0">
                    Capture incredible new insights by exploring user
                    expressions
                  </p>
                  <p className="" id="loginCheckText">
                    Please check your email for a login link. If you have not
                    received anything, please check your spam folder.
                  </p>
                  <p className="boldText" id="problemsText">
                    Having problems? Request a new{` `}
                    <button
                      className="boldText"
                      id="newLinkButton"
                      onClick={() => setMagicLinkSent(false)}
                    >
                      link
                    </button>
                    .
                  </p>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: 16,
                    }}
                  >
                    <LoadingButton
                      type="submit"
                      name="google-signUp"
                      id="googleButton"
                      onClick={() => setClickedButton('google')}
                      loading={loading}
                    >
                      <img
                        src={google_webp}
                        alt={'Google Icon'}
                        className="googleIcon"
                        width={'20vw'}
                        onError={(e) => {
                          e.currentTarget.onerror = null; // Prevents infinite loop if the fallback also fails
                          e.currentTarget.src = google_png; // Fallback to the original JPG or PNG
                        }}
                      />
                      Sign up with Google
                    </LoadingButton>
                  </div>
                </>
              ) : (
                <>
                  <p className="boldText" id="popupLogoHeaderText">
                    Continue with email
                  </p>
                  <p className="" id="loginCheckText">
                    Please check your email for a login link. If you have not
                    received anything, please check your spam folder.
                  </p>
                  <p className="boldText" id="problemsText">
                    Having problems? Request a new{` `}
                    <button
                      className="boldText"
                      id="newLinkButton"
                      onClick={() => setMagicLinkSent(false)}
                    >
                      link
                    </button>
                    .
                  </p>
                </>
              )
            ) : (
              <p className="boldText" id="popupLogoHeaderText">
                Continue with email
              </p>
            )}
            {!magicLinkSent ? (
              <div id="formSection">
                <form onSubmit={handleFormSubmit} id="popupForm">
                  {emailInvalid !== true ? (
                    <>
                      <input
                        id="emailInput"
                        type="text"
                        name="email"
                        value={email}
                        placeholder="Email"
                        onChange={(e) =>
                          setEmail(normalizeEmail(e.target.value))
                        }
                      />
                      {props.mode === 'login' && (
                        <div style={{ display: 'flex', alignItems: 'center', margin: '8px 0' }}>
                          <input
                            type="checkbox"
                            id="keepMeSignedIn"
                            checked={keepMeSignedIn}
                            onChange={e => setKeepMeSignedIn(e.target.checked)}
                            style={{ marginRight: 8 }}
                          />
                          <label htmlFor="keepMeSignedIn" style={{ fontSize: 14 }}>
                            Keep me signed in
                          </label>
                        </div>
                      )}
                      <p>
                        An email will be sent to your inbox for verification
                        purposes.
                      </p>
                    </>
                  ) : (
                    <>
                      <input
                        id="emailInvalidInput"
                        type="text"
                        value={email}
                        placeholder="Email"
                        onChange={(e) =>
                          setEmail(normalizeEmail(e.target.value))
                        }
                      />
                      <p className="redText" id="invalidEmailText">
                        Invalid email. Please ensure your email is correct.
                      </p>
                    </>
                  )}
                  {props.mode == 'signUp' && (
                    <div className="rowArrangement" id="agreeSection">
                      <input
                        type="checkbox"
                        name={`_agree`}
                        id="agreeCheckbox"
                        value={'unchecked'}
                      />
                      <p>
                        I agree to{' '}
                        <span className="boldText">BLUESKEYE AI</span>
                        's
                        <Link
                          className="policyLink boldText"
                          to={'/privacyPolicy'}
                        >
                          Terms of Service
                        </Link>
                        ,{' '}
                        <Link
                          className="policyLink boldText"
                          to={'/privacyPolicy'}
                        >
                          Privacy Policy
                        </Link>{' '}
                        and{' '}
                        <Link
                          className="policyLink boldText"
                          to={'/privacyPolicy'}
                        >
                          Data Processing Agreement
                        </Link>
                        .
                      </p>
                    </div>
                  )}
                  {props.mode == 'signUp' && agreed == false && (
                    <p className="redText">
                      Please check the box above to proceed.
                    </p>
                  )}
                  {props.mode == 'signUp' ? (
                    <>
                      <LoadingButton
                        type="submit"
                        className="popupModalButton"
                        onClick={() => setClickedButton('create')}
                        loading={loading}
                      >
                        Create my account
                      </LoadingButton>
                    </>
                  ) : (
                    <LoadingButton
                      type="submit"
                      className="popupModalButton"
                      onClick={() => setClickedButton('signIn')}
                      loading={loading}
                    >
                      Continue
                    </LoadingButton>
                  )}
                  <hr className="midwide modal"></hr>
                  <LoadingButton
                    type="submit"
                    name="google-signUp"
                    id="googleButton"
                    onClick={() => setClickedButton('google')}
                    loading={loading}
                  >
                    <img
                      src={google_webp}
                      alt={'Google Icon'}
                      className="googleIcon"
                      width={'20vw'}
                      onError={(e) => {
                        e.currentTarget.onerror = null; // Prevents infinite loop if the fallback also fails
                        e.currentTarget.src = google_png; // Fallback to the original JPG or PNG
                      }}
                    />
                    {props.mode == 'signUp'
                      ? ` Sign up with Google`
                      : ` Sign in with Google`}
                  </LoadingButton>
                </form>
              </div>
            ) : (
              <div id="formSection"></div>
            )}
            {magicLinkSent && (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16,
                }}
              >
                <LoadingButton
                  type="submit"
                  name="google-signUp"
                  id="googleButton"
                  onClick={() => setClickedButton('google')}
                  loading={loading}
                >
                  <img
                    src={google_webp}
                    alt={'Google Icon'}
                    className="googleIcon"
                    width={'20vw'}
                    onError={(e) => {
                      e.currentTarget.onerror = null; // Prevents infinite loop if the fallback also fails
                      e.currentTarget.src = google_png; // Fallback to the original JPG or PNG
                    }}
                  />
                  {props.mode == 'signUp'
                    ? `Sign up with Google`
                    : `Sign in with Google`}
                </LoadingButton>
              </div>
            )}
          </div>
        </div>
        <div className="popup-right">
          <PopupCarousel />
        </div>
      </div>
    </div>
  );
};
